import { Chip as MuiChip, chipClasses as MuiChipClasses } from '@mui/material';
import { clsx } from 'clsx';
import { Close as CloseIcon } from 'mdi-material-ui';
import React, { forwardRef } from 'react';
import { combineSx, SxProps } from '../../utils';

type NativeDivProps = React.ComponentPropsWithoutRef<'div'>;

export interface ChipProps extends Omit<NativeDivProps, 'children' | 'color'> {
  /**
   * The content of the component.
   */
  label: React.ReactNode;
  /**
   * If true, the component is disabled.
   * @default false
   */
  disabled?: boolean;
  /**
   * Icon element.
   */
  Icon?: React.ReactElement;
  /**
   * Callback fired when the chip is clicked.
   */
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  /**
   * Callback fired when the delete icon is clicked.
   * If set, the delete icon will be shown.
   */
  onDelete?: React.MouseEventHandler<HTMLDivElement>;
  /**
   * Whether or not the chip can be selected.
   * Use together with the `selected` and `onClick` props to create a selectable chip.
   * @default false
   */
  selectable?: boolean;
  /**
   * Controls whether the chip is currently selected.
   * Use together with the `selectable` and `onClick` props to create a selectable chip.
   * @default false
   */
  selected?: boolean;
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps;
}

export const chipClasses = {
  label: MuiChipClasses.label,
  // MUI doesn't have a selectable chip variant so we create our own
  selectable: 'Chip-selectable',
};

export const Chip = forwardRef<HTMLDivElement, ChipProps>(function Chip(
  { disabled = false, selectable = false, selected = false, sx, Icon, className, ...props },
  ref
) {
  return (
    <MuiChip
      deleteIcon={<CloseIcon />}
      disabled={disabled}
      size="small"
      icon={Icon}
      {...props}
      className={clsx(className, selectable && chipClasses.selectable)}
      variant={selectable && !selected ? 'outlined' : 'filled'}
      sx={combineSx(
        (theme) => ({
          borderRadius: theme.featureFlags.panwRebrandStubYear ? theme.shape.borderRadiusXs : undefined,
        }),
        sx
      )}
      ref={ref}
    />
  );
});
